import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    AppBar, Toolbar, Typography, Box, Button, Container, Avatar,
    IconButton, Tooltip, Link as MuiLink, alpha, useMediaQuery, Menu, MenuItem, CircularProgress
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import logoUrl from '../assets/Logo.png';
import playstoreIcon from '../assets/play-store-icon.png';
import appstoreIcon from '../assets/appstore-icon.png';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Dialog, DialogTitle, DialogContent, TextField, DialogActions,
    Snackbar, Alert, Select, FormControl, InputLabel
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import { inviteFamilyMember, inviteFriend, getUserProfile, upgradeToFamilyAccount } from '../api/apiClient';
import CountryFlag from './CountryFlag';
import { useUser } from '../UserContext';
import DeleteDataModal from './DeleteDataModal';
import { setMonthlyExpensesCache } from '../utils/monthlyExpenses';

export const standardPagePadding = { xs: 2, sm: 3, md: 3, lg: 5 };

// Mapping from full country names to 2-letter codes
const COUNTRY_NAME_TO_CODE = {
  'India': 'IN',
  'United States': 'US',
  'United Kingdom': 'GB',
  'China': 'CN',
  'Japan': 'JP',
  'Germany': 'DE',
  'Brazil': 'BR',
  'Indonesia': 'ID',
  'Russia': 'RU',
  'Mexico': 'MX',
  'Vietnam': 'VN',
  'South Korea': 'KR',
  'France': 'FR',
  'Canada': 'CA',
  'Australia': 'AU',
  'Spain': 'ES',
  'Italy': 'IT',
  'Philippines': 'PH',
  'Thailand': 'TH',
  'Saudi Arabia': 'SA',
  'UAE': 'AE',
  'Poland': 'PL',
  'Argentina': 'AR',
  'Egypt': 'EG',
  'Netherlands': 'NL',
  'Sweden': 'SE',
  'Belgium': 'BE',
  'Singapore': 'SG'
};

const HeaderSection = ({ user, onLogout, pageType, hideFlag }) => {
    const { country: userCountry } = useUser();
    // Ensure userCountry always has a valid value for flag display
    // Convert full country name to 2-letter code if needed
    const normalizedCountry = COUNTRY_NAME_TO_CODE[userCountry] || userCountry;
    const displayCountry = normalizedCountry || 'IN';
    
    
    const navigate = useNavigate();
    const theme = useTheme();
                        const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const firstName = user?.firstName || localStorage.getItem('first_name');
    const lastName = user?.lastName || localStorage.getItem('last_name');
    const userId = user?.id || localStorage.getItem('user_id');
    const primaryColor = theme.palette.primary.main;
    const darkBlueText = '#0D1B3E';
    const avatarGradient = `linear-gradient(45deg, ${theme.palette.primary.light} 30%, ${theme.palette.primary.main} 90%)`;
    const getInitials = (name) => name ? name.charAt(0).toUpperCase() : '?';
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const relationships = ['Spouse', 'Son', 'Daughter', 'Parent', 'Other'];
    const [inviteEmail, setInviteEmail] = useState('');
    const [relation, setRelation] = useState('');
    const [customRelation, setCustomRelation] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [errors, setErrors] = useState({
        email: '',
        relation: '',
        customRelation: ''
    });
    const [loading, setLoading] = useState(false);
    const [accountInfo, setAccountInfo] = useState({
        accountType: user?.account_type || localStorage.getItem('account_type'),
        roleInFamily: user?.role_in_family || localStorage.getItem('role_in_family')
    });
    const accountType = accountInfo.accountType;
    const roleInFamily = accountInfo.roleInFamily;
    const [inviteFriendDialogOpen, setInviteFriendDialogOpen] = useState(false);
    const [inviteFriendEmail, setInviteFriendEmail] = useState('');
    const [friendInviteLoading, setFriendInviteLoading] = useState(false);
    const [friendInviteError, setFriendInviteError] = useState('');
    const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState(null);
    const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
    const [deleteDataModalOpen, setDeleteDataModalOpen] = useState(false);
    const [upgradeProcessing, setUpgradeProcessing] = useState(false);
    // Legacy subscription cancellation tracking is disabled now that the plan is free.

    const isProfileMenuOpen = Boolean(profileMenuAnchorEl);

    useEffect(() => {
        setAccountInfo(prev => ({
            ...prev,
            accountType: user?.account_type || localStorage.getItem('account_type'),
            roleInFamily: user?.role_in_family || localStorage.getItem('role_in_family')
        }));
    }, [user?.account_type, user?.role_in_family]);

    // useEffect(() => {
    //     const status =
    //         (user?.subscription_status || localStorage.getItem('subscription_status') || '').toLowerCase();
    //     setIsSubscriptionCancelled(status === 'cancelled');
    // }, [user?.subscription_status]);
    
    // App Store redirect handler
    const handleStoreClick = (event) => {
        if (event && event.preventDefault) event.preventDefault();
        window.open('https://apps.apple.com/us/app/receiptsnap-ai/id6753581452', '_blank', 'noopener,noreferrer');
    };

    // Listen for global 'openInviteFriend' events so other pages (e.g., Home banner CTA)
    // can open the same Invite a Friend dialog without duplicating code.
    useEffect(() => {
        const openInviteHandler = () => setInviteFriendDialogOpen(true);
        window.addEventListener('openInviteFriend', openInviteHandler);
        return () => window.removeEventListener('openInviteFriend', openInviteHandler);
    }, []);

    const handleEmailChange = (e) => {
        setInviteEmail(e.target.value);
        if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
    };

    const handleRelationChange = (e) => {
        setRelation(e.target.value);
        if (errors.relation) setErrors(prev => ({ ...prev, relation: '' }));
        if (e.target.value !== 'Other') {
            setCustomRelation('');
            setErrors(prev => ({ ...prev, customRelation: '' }));
        }
    };

    const handleCustomRelationChange = (e) => {
        setCustomRelation(e.target.value);
        if (errors.customRelation) setErrors(prev => ({ ...prev, customRelation: '' }));
    };

    const handleSendInvite = async () => {
        const trimmedEmail = inviteEmail.trim().toLowerCase();
        const trimmedCustomRelation = customRelation.trim();
        const newErrors = {
            email: '',
            relation: '',
            customRelation: ''
        };
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!trimmedEmail) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(trimmedEmail)) {
            newErrors.email = 'Enter a valid email';
        }
        if (!relation) {
            newErrors.relation = 'Relationship is required';
        }
        if (relation === 'Other') {
            if (!trimmedCustomRelation) {
                newErrors.customRelation = 'Please specify the relationship';
            } else if (trimmedCustomRelation.length > 25) {
                newErrors.customRelation = 'Must be under 25 characters';
            }
        }
        if (Object.values(newErrors).some(Boolean)) {
            setErrors(newErrors);
            return;
        }
        setErrors({ email: '', relation: '', customRelation: '' });
        const inviter_user_id = localStorage.getItem('user_id');
        if (!inviter_user_id) {
            setSnackbar({
                open: true,
                message: 'User not identified. Please login again.',
                severity: 'error'
            });
            return;
        }
        const payload = {
            inviter_user_id,
            invitee_email: trimmedEmail,
            relation: relation === 'Other' ? trimmedCustomRelation.toLowerCase() : relation.toLowerCase(),
        };
        setLoading(true);
        try {
            const response = await inviteFamilyMember(payload);
            const successMsg = response?.data?.message || 'Invite sent successfully!';
            setSnackbar({ open: true, message: successMsg, severity: 'success' });
            setInviteDialogOpen(false);
            setInviteEmail('');
            setRelation('');
            setCustomRelation('');
        } catch (error) {
            let errorMessage = error?.response?.data?.error || error?.message || 'Failed to send invite.';
            if (errorMessage.includes('already associated with an existing account')) {
                errorMessage = 'This email is already associated with an existing account.';
            }
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFriendEmailChange = (e) => {
        setInviteFriendEmail(e.target.value);
        if (friendInviteError) setFriendInviteError('');
    };

    const handleSendFriendInvite = async () => {
        const trimmedEmail = inviteFriendEmail.trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!trimmedEmail) {
            setFriendInviteError('Email is required');
            return;
        } else if (!emailRegex.test(trimmedEmail)) {
            setFriendInviteError('Enter a valid email');
            return;
        }
        setFriendInviteError('');
        const inviter_user_id = localStorage.getItem('user_id');
        if (!inviter_user_id) {
            setSnackbar({
                open: true,
                message: 'User not identified. Please login again.',
                severity: 'error'
            });
            return;
        }
        setFriendInviteLoading(true);
        try {
            const response = await inviteFriend({ inviter_user_id, invitee_email: trimmedEmail });
            const successMsg = response?.data?.message || 'Friend invite sent successfully!';
            setSnackbar({ open: true, message: successMsg, severity: 'success' });
            setInviteFriendDialogOpen(false);
            setInviteFriendEmail('');
        } catch (error) {
            let errorMessage = error?.response?.data?.error || error?.message || 'Failed to send friend invite.';
            if (errorMessage.includes('already associated with an existing account')) {
                errorMessage = 'This email is already associated with an existing account.';
            }
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setFriendInviteLoading(false);
        }
    };

    const handleProfileMenuOpen = (event) => {
        setProfileMenuAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchorEl(null);
    };

    const openUpgradeFlow = () => {
        if ((accountType || '').toLowerCase() === 'family') {
            setSnackbar({
                open: true,
                message: 'Your account is already on the Family plan.',
                severity: 'info'
            });
            return;
        }
        setUpgradeConfirmOpen(true);
    };

    const handleFamilyDashboardSelect = () => {
        handleProfileMenuClose();
        navigate('/family-dashboard');
    };

    const handleUpgradeMenuSelect = () => {
        handleProfileMenuClose();
        openUpgradeFlow();
    };

    const handleMobileUpgradeSelect = () => {
        handleMenuClose();
        openUpgradeFlow();
    };

    const handleMobileFamilyDashboardSelect = () => {
        handleMenuClose();
        navigate('/family-dashboard');
    };

    const handleEditProfileMenuSelect = () => {
        handleProfileMenuClose();
        navigate('/user-profile');
    };

    const handleMobileEditProfileSelect = () => {
        handleMenuClose();
        navigate('/user-profile');
    };

    const handleDeleteDataMenuSelect = () => {
        handleProfileMenuClose();
        setDeleteDataModalOpen(true);
    };

    const handleMobileDeleteDataSelect = () => {
        handleMenuClose();
        setDeleteDataModalOpen(true);
    };

    const handleConfirmUpgrade = async () => {
        if (!userId) {
            setSnackbar({
                open: true,
                message: 'User not identified. Please login again.',
                severity: 'error'
            });
            setUpgradeConfirmOpen(false);
            return;
        }
        setUpgradeProcessing(true);
        try {
            const response = await upgradeToFamilyAccount(userId);
            if (response?.data?.success) {
                const updatedUser = response.data.user || {};
                localStorage.setItem('account_type', updatedUser.account_type || 'Family');
                if (updatedUser.family_id) {
                    localStorage.setItem('family_id', updatedUser.family_id);
                }
                if (updatedUser.role_in_family) {
                    localStorage.setItem('role_in_family', updatedUser.role_in_family);
                }
                setAccountInfo(prev => ({
                    ...prev,
                    accountType: updatedUser.account_type || 'Family',
                    roleInFamily: updatedUser.role_in_family || prev.roleInFamily
                }));
                setSnackbar({
                    open: true,
                    severity: 'success',
                    message: 'Account upgraded to Family successfully!'
                });
            } else {
                throw new Error(response?.data?.error || 'Failed to upgrade account.');
            }
        } catch (error) {
            console.error('Upgrade to family error:', error);
            setSnackbar({
                open: true,
                severity: 'error',
                message: error?.response?.data?.error || error?.message || 'Failed to upgrade account.'
            });
        } finally {
            setUpgradeProcessing(false);
            setUpgradeConfirmOpen(false);
        }
    };

    const navLinkSx = {
        display: 'inline-block',
        padding: theme.spacing(0.5, 1.5),
        borderRadius: '4px',
        fontFamily: "'Inter', sans-serif",
        fontWeight: 500,
        fontSize: '1rem',
        textDecoration: 'none',
        color: darkBlueText,
        transition: theme.transitions.create(['font-weight', 'color'], {
            duration: theme.transitions.duration.short
        }),
        '&.active': {
            fontWeight: 600,
            color: primaryColor
        },
        '&:hover': {
            textDecoration: 'none',
            color: primaryColor
        }
    };

    const handleLogoutClick = () => {
        console.log("Header Logout Clicked");
        if (onLogout && typeof onLogout === 'function') {
            onLogout();
        } else {
            console.warn("onLogout prop missing/not function. Default logout.");
            localStorage.clear();
            navigate('/login');
        }
    };

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <AppBar
            position="fixed"
            color="transparent"
            elevation={0}
            sx={{
                top: 0,
                left: 0,
                right: 0,
                width: '100%',
                fontFamily: "'Inter', sans-serif",
                zIndex: 1200,
                backdropFilter: 'blur(8px)',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
            }}
        >
            <Container maxWidth="xl" sx={{ px: standardPagePadding }}>
                <Toolbar
                    disableGutters
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: { xs: 64, sm: 70, md: 90 },
                        position: 'relative'
                    }}
                >
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        justifyContent: 'flex-start'
                    }}>
                        <MuiLink
                            component={NavLink}
                            to={userId ? "/home" : "/login"}
                            aria-label="Go to homepage"
                            sx={{ display: 'flex', alignItems: 'center', '&:hover': { textDecoration: 'none' } }}
                        >
                            <img
                                src={logoUrl}
                                alt="ReceiptSnap Logo"
                                style={{ height: '70px', width: 'auto', objectFit: 'contain' }}
                            />
                            {(displayCountry && pageType !== 'login' && pageType !== 'signup' && !hideFlag) ? (
                                <CountryFlag 
                                    countryCode={displayCountry} 
                                    size={'2.0rem'}

                                  
                                />
                            ) : null}
                        </MuiLink>
                    </Box>
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: { xs: 'none', md: 'flex' },
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: { md: theme.spacing(1.0), lg: theme.spacing(1.6) },
                            mx: { md: 2, lg: 4 },
                            minWidth: 0, // Allow shrinking
                            overflow: 'hidden'
                        }}
                    >
                        {userId && (
                            <>
                                <MuiLink component={NavLink} to="/home" sx={{
                                    ...navLinkSx,
                                    whiteSpace: 'nowrap',
                                    fontSize: { md: '0.9rem', lg: '1rem' },
                                    px: { md: 1, lg: 1.5 }
                                }}>Home</MuiLink>
                                <MuiLink component={NavLink} to="/expenses" sx={{
                                    ...navLinkSx,
                                    whiteSpace: 'nowrap',
                                    fontSize: { md: '0.9rem', lg: '1rem' },
                                    px: { md: 1, lg: 1.5 }
                                }}>Expenses</MuiLink>
                                <MuiLink component={NavLink} to="/reports" sx={{
                                    ...navLinkSx,
                                    whiteSpace: 'nowrap',
                                    fontSize: { md: '0.9rem', lg: '1rem' },
                                    px: { md: 1, lg: 1.5 }
                                }}>Reports</MuiLink>
                                <MuiLink component={NavLink} to="/faq" sx={{
                                    ...navLinkSx,
                                    whiteSpace: 'nowrap',
                                    fontSize: { md: '0.9rem', lg: '1rem' },
                                    px: { md: 1, lg: 1.5 }
                                }}>FAQ</MuiLink>
                            </>
                        )}
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 1.5, md: 0.8, lg: 1.5 },
                        flexShrink: 0,
                        minWidth: 'fit-content',
                        justifyContent: 'flex-end'
                    }}>

                        <Button 
                            component="a"
                            href="https://play.google.com/store/apps/details?id=com.rsnap"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                                display: (pageType === 'login' || pageType === 'signup') ? 'none' : 'inline-flex',
                                marginRight: '-2.5rem',
                                '&:hover': { 
                                    backgroundColor: 'transparent' 
                                },
                                '&:focus': { 
                                    backgroundColor: 'transparent' 
                                },
                                '&:active': { 
                                    backgroundColor: 'transparent' 
                                }
                            }}
                        >
                            <img src={playstoreIcon} alt="Play Store Icon" style={{ width: '40px', height: '40px' }} />
                        </Button>

                        <Button 
                            onClick={handleStoreClick}
                            sx={{ 
                                display: (pageType === 'login' || pageType === 'signup') ? 'none' : 'inline-flex',
                                marginRight: '-0.5rem',
                                '&:hover': { 
                                    backgroundColor: 'transparent' 
                                },
                                '&:focus': { 
                                    backgroundColor: 'transparent' 
                                },
                                '&:active': { 
                                    backgroundColor: 'transparent' 
                                }
                            }}
                        >
                            <img src={appstoreIcon} alt="App Store Icon" style={{ width: '40px', height: '40px' }} />
                        </Button>


                       
                        {userId && accountType === 'Family' && roleInFamily === 'owner' && (
                            <Button
                                onClick={() => setInviteDialogOpen(true)}
                                startIcon={<GroupAddIcon />}
                                sx={{
                                    //display: { xs: 'none', sm: 'none', md: 'inline-flex' },
                                    display: { xs: 'none', sm: 'none', md: 'none', lg: 'inline-flex'},
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { md: '0.7rem', lg: '0.75rem' },
                                    px: { md: 1, lg: 1.5 },
                                    py: 0.5,
                                    borderRadius: '999px',
                                    border: '2px solid',
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    zIndex: 1,
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        color: '#fff',
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                    }
                                }}
                            >
                                Invite Member
                            </Button>
                        )}
                        {userId && (
                            <Button
                                onClick={() => setInviteFriendDialogOpen(true)}
                                startIcon={<PersonIcon />}
                                sx={{
                                    display: { xs: 'none', sm: 'none', md: 'inline-flex' },
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: { md: '0.7rem', lg: '0.75rem' },
                                    px: { md: 1, lg: 1.5 },
                                    py: 0.5,
                                    borderRadius: '999px',
                                    border: '2px solid',
                                    borderColor: theme.palette.primary.main,
                                    color: theme.palette.primary.main,
                                    backgroundColor: '#fff',
                                    boxShadow: 'none',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    zIndex: 1,
                                    ml: { md: 0.5, lg: 1 },
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        color: '#fff',
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                    }
                                }}
                            >
                                Invite a Friend
                            </Button>
                        )}
                        {userId ? (
                            <>
                                <Box
                                    onClick={handleProfileMenuOpen}
                                    sx={{
                                        display: { xs: 'none', md: 'flex' },
                                        alignItems: 'center',
                                        mr: 0.5,
                                        cursor: 'pointer',
                                        minWidth: 0,
                                        '&:hover': {
                                            opacity: 0.85,
                                        },
                                    }}
                                >
                                    <Avatar sx={{
                                        background: avatarGradient,
                                        width: { md: 32, lg: 36 },
                                        height: { md: 32, lg: 36 },
                                        mr: { md: 0.5, lg: 1 },
                                        fontSize: { md: '0.8rem', lg: '1rem' },
                                        flexShrink: 0
                                    }}>
                                        {getInitials(firstName)}
                                    </Avatar>
                                    <Typography
                                        variant="body1"
                                        sx={{ 
                                            fontWeight: 600, 
                                            color: darkBlueText, 
                                            whiteSpace: 'nowrap',
                                            fontSize: { md: '0.85rem', lg: '1rem' },
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: { md: '120px', lg: '150px' }
                                        }}
                                    >
                                        {`${firstName || ''} ${lastName || ''}`.trim() || 'User'}
                                    </Typography>
                                </Box>
                                <Menu
                                    anchorEl={profileMenuAnchorEl}
                                    open={isProfileMenuOpen}
                                    onClose={handleProfileMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    PaperProps={{
                                        elevation: 3,
                                        sx: {
                                            borderRadius: 2,
                                            minWidth: 220,
                                            py: 0.5
                                        }
                                    }}
                                >
                                    {accountType === 'Family' && roleInFamily === 'owner' && (
                                        <MenuItem onClick={handleFamilyDashboardSelect}>
                                            Family Dashboard
                                        </MenuItem>
                                    )}
                                    <MenuItem onClick={handleUpgradeMenuSelect}>
                                        Upgrade to Family
                                    </MenuItem>
                                    <MenuItem onClick={handleEditProfileMenuSelect}>
                                        Edit User Profile
                                    </MenuItem>
                                    <MenuItem
                                        onClick={handleDeleteDataMenuSelect}
                                        sx={{ color: 'error.main' }}
                                    >
                                        Delete My Data
                                    </MenuItem>
                                </Menu>
                                <Tooltip title="Logout">
                                    <IconButton
                                        onClick={handleLogoutClick}
                                        aria-label="Logout"
                                        sx={{
                                            color: primaryColor,
                                            '&:hover': {
                                                backgroundColor: alpha(primaryColor, 0.1),
                                                color: theme.palette.primary.dark
                                            }
                                        }}
                                    >
                                        <LogoutIcon fontSize={isSmallMobile ? "small" : "medium"} />
                                    </IconButton>
                                </Tooltip>
                            </>
                        ) : (
                            !isMobile && pageType !== 'login' && pageType !== 'signup' && pageType !== 'public' && (
                                <Button
                                    component={NavLink}
                                    to="/login"
                                    variant="contained"
                                    size="small"
                                    aria-label="Sign In"
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                        fontFamily: "'Inter', sans-serif",
                                        whiteSpace: 'nowrap',
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 3,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #00c6fb 100%)`,
                                        boxShadow: '0 2px 8px rgba(0,122,255,0.10)',
                                        '&:hover': {
                                            background: `linear-gradient(90deg, #0056b3 0%, #00c6fb 100%)`,
                                        }
                                    }}
                                    color="primary"
                                >
                                    Sign In
                                </Button>
                            )
                        )}
                        {isMobile && userId && (
                            <>
                                <IconButton
                                    color="inherit"
                                    aria-label="open mobile menu"
                                    onClick={handleMenuClick}
                                    sx={{ color: darkBlueText }}
                                >
                                    <MenuIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    PaperProps={{
                                        elevation: 3,
                                        sx: {
                                            borderRadius: 1,
                                            mt: 1,
                                            minWidth: 150,
                                            boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    <MenuItem
                                        component={NavLink}
                                        to="/home"
                                        onClick={handleMenuClose}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&.active': {
                                                color: 'primary.main',
                                                fontWeight: 700,
                                            }
                                        }}
                                        className={({ isActive }) => (isActive ? 'active' : '')}
                                    >
                                        Home
                                    </MenuItem>
                                    <MenuItem
                                        component={NavLink}
                                        to="/expenses"
                                        onClick={handleMenuClose}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&.active': {
                                                color: 'primary.main',
                                                fontWeight: 700,
                                            }
                                        }}
                                        className={({ isActive }) => (isActive ? 'active' : '')}
                                    >
                                        Expenses
                                    </MenuItem>
                                    <MenuItem
                                        component={NavLink}
                                        to="/reports"
                                        onClick={handleMenuClose}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&.active': {
                                                color: 'primary.main',
                                                fontWeight: 700,
                                            }
                                        }}
                                        className={({ isActive }) => (isActive ? 'active' : '')}
                                    >
                                        Reports
                                    </MenuItem>
                                    <MenuItem
                                        component={NavLink}
                                        to="/faq"
                                        onClick={handleMenuClose}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&.active': {
                                                color: 'primary.main',
                                                fontWeight: 700,
                                            }
                                        }}
                                        className={({ isActive }) => (isActive ? 'active' : '')}
                                    >
                                        FAQ
                                    </MenuItem>


                                    
                                    



                                    {accountType === 'Family' && roleInFamily === 'owner' && (
                                        <MenuItem
                                            onClick={() => {
                                                handleMenuClose();
                                                setInviteDialogOpen(true);
                                            }}
                                            sx={{
                                                fontWeight: 300,
                                                color: 'primary.main',
                                                '&:hover': { color: 'primary.dark' }
                                            }}
                                        >
                                             Invite Member
                                        </MenuItem>
                                    )}
                                    <MenuItem
                                        onClick={() => {
                                            handleMenuClose();
                                            setInviteFriendDialogOpen(true);
                                        }}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                    >
                                        Invite a Friend
                                    </MenuItem>
                                    {accountType === 'Family' && roleInFamily === 'owner' && (
                                        <MenuItem
                                            onClick={handleMobileFamilyDashboardSelect}
                                            sx={{
                                                fontWeight: 300,
                                                color: 'primary.main',
                                                '&:hover': { color: 'primary.dark' }
                                            }}
                                        >
                                            Family Dashboard
                                        </MenuItem>
                                    )}
                                    <MenuItem
                                        onClick={handleMobileUpgradeSelect}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                    >
                                        Upgrade to Family
                                    </MenuItem>
                                    <MenuItem
                                        onClick={handleMobileEditProfileSelect}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'primary.main',
                                            '&:hover': { color: 'primary.dark' }
                                        }}
                                    >
                                        Edit User Profile
                                    </MenuItem>
                                    <MenuItem
                                        onClick={handleMobileDeleteDataSelect}
                                        sx={{
                                            fontWeight: 300,
                                            color: 'error.main',
                                            '&:hover': { color: 'error.dark' }
                                        }}
                                    >
                                        Delete My Data
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                </Toolbar>
            </Container>
            <Dialog open={inviteDialogOpen} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, position: 'relative', pr: 5 }}>
                    Invite Family Members
                    <IconButton
                        onClick={() => setInviteDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                            '&:hover': { color: theme.palette.primary.main }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" textAlign="center" color="text.secondary">
                        Invite family members to your plan and manage receipts together.
                    </Typography>
                    <TextField
                        label="Family Member Email"
                        fullWidth
                        value={inviteEmail}
                        onChange={handleEmailChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={loading}
                        InputProps={{
                            startAdornment: <EmailIcon sx={{ mr: 1 }} />
                        }}
                        type="email"
                    />
                    <FormControl fullWidth error={!!errors.relation} disabled={loading}>
                        <InputLabel>Relationship</InputLabel>
                        <Select
                            value={relation}
                            label="Relationship"
                            onChange={handleRelationChange}
                            startAdornment={<PersonIcon sx={{ mr: 1, ml: 1 }} />}
                        >
                            {relationships.map((r) => (
                                <MenuItem key={r} value={r}>{r}</MenuItem>
                            ))}
                        </Select>
                        {errors.relation && (
                            <Typography variant="caption" color="error">{errors.relation}</Typography>
                        )}
                    </FormControl>
                    {relation === 'Other' && (
                        <TextField
                            label="Specify Relationship"
                            fullWidth
                            value={customRelation}
                            onChange={handleCustomRelationChange}
                            error={!!errors.customRelation}
                            helperText={errors.customRelation}
                            inputProps={{ maxLength: 25 }}
                            disabled={loading}
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleSendInvite}
                        fullWidth
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={18} color="inherit" />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            py: 1.3,
                            borderRadius: '12px',
                            background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: '#fff',
                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                background: `linear-gradient(145deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        {loading ? 'Sending...' : 'Send Invite'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={inviteFriendDialogOpen} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 700, position: 'relative', pr: 5 }}>
                    Invite a Friend
                    <IconButton
                        onClick={() => setInviteFriendDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                            '&:hover': { color: theme.palette.secondary.main }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" textAlign="center" color="text.secondary">
                        Invite a friend to try ReceiptSnap AI and manage receipts with ease.
                    </Typography>
                    <TextField
                        label="Friend's Email"
                        fullWidth
                        value={inviteFriendEmail}
                        onChange={handleFriendEmailChange}
                        error={!!friendInviteError}
                        helperText={friendInviteError}
                        disabled={friendInviteLoading}
                        InputProps={{
                            startAdornment: <EmailIcon sx={{ mr: 1 }} />
                        }}
                        type="email"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleSendFriendInvite}
                        fullWidth
                        disabled={friendInviteLoading}
                        startIcon={friendInviteLoading && <CircularProgress size={18} color="inherit" />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.95rem',
                            py: 1.3,
                            borderRadius: '12px',
                            background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: '#fff',
                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                background: `linear-gradient(145deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        {friendInviteLoading ? 'Sending...' : 'Send Invite'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={upgradeConfirmOpen}
                onClose={() => !upgradeProcessing && setUpgradeConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    Upgrade to Family
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        Upgrade your account to the Family plan to invite members and manage receipts together.
                        This action will take effect immediately.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setUpgradeConfirmOpen(false)}
                        disabled={upgradeProcessing}
                        sx={{ textTransform: 'none' }}
                    >
                        Not Now
                    </Button>
                    <Button
                        onClick={handleConfirmUpgrade}
                        disabled={upgradeProcessing}
                        variant="contained"
                        sx={{ textTransform: 'none' }}
                        startIcon={upgradeProcessing ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {upgradeProcessing ? 'Upgrading...' : 'Confirm Upgrade'}
                    </Button>
                </DialogActions>
            </Dialog>
            <DeleteDataModal
                open={deleteDataModalOpen}
                onClose={() => setDeleteDataModalOpen(false)}
                user={{ user_id: userId, id: userId }}
                onSuccess={() => {
                    setDeleteDataModalOpen(false);
                    try {
                        setMonthlyExpensesCache({
                            total: 0,
                            totalReceipts: 0,
                            categories: [],
                        });
                    } catch (e) {
                        console.error('Failed to reset monthly_expenses from HeaderSection:', e);
                    }
                    setSnackbar({
                        open: true,
                        severity: 'success',
                        message: 'Your data deletion request has been received.'
                    });
                }}
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            
        </AppBar>
    );
};

export default HeaderSection;
